import React, { useState, useEffect, useRef } from 'react';
import { useFirestoreConnect, isLoaded } from 'react-redux-firebase';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import { Search, Explore, LocationOn, Star } from '@mui/icons-material';
import SEO from '../components/SEO';

const STORE_KEY_BIZ = 'venues_search_biz';
const STORE_KEY_IMP = 'venues_search_imp';
const RESULT_LIMIT = 100;

const AUSTRALIAN_STATES = [
  { value: 'Australian Capital Territory', label: 'ACT' },
  { value: 'New South Wales', label: 'NSW' },
  { value: 'Northern Territory', label: 'NT' },
  { value: 'Queensland', label: 'QLD' },
  { value: 'South Australia', label: 'SA' },
  { value: 'Tasmania', label: 'TAS' },
  { value: 'Victoria', label: 'VIC' },
  { value: 'Western Australia', label: 'WA' },
];

const toTitleCase = (str) =>
  str
    .toLowerCase()
    .replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());

const VenuesPage = () => {
  const history = useHistory();
  const [state, setState] = useState('');
  const [suburb, setSuburb] = useState('');
  const [venueName, setVenueName] = useState('');
  const [activeQuery, setActiveQuery] = useState(null);

  const resultsRef = useRef(null);

  useEffect(() => {
    document.title = 'Explore Venues | Piatto';
  }, []);

  // Build Firestore where clause for a given state field name.
  // Only one range (prefix) filter allowed per query, so suburb takes
  // precedence; venue name is filtered client-side when both are given.
  const buildWhere = (stateField) => {
    if (!activeQuery) return null;
    const where = [];
    if (activeQuery.state) {
      where.push([stateField, '==', activeQuery.state]);
    }
    if (activeQuery.suburb) {
      where.push(['bizSuburb', '>=', activeQuery.suburb]);
      where.push(['bizSuburb', '<=', activeQuery.suburb + '\uf8ff']);
    } else if (activeQuery.name) {
      where.push(['bizName', '>=', activeQuery.name]);
      where.push(['bizName', '<=', activeQuery.name + '\uf8ff']);
    }
    return where;
  };

  const firestoreQueries = [];
  if (activeQuery) {
    firestoreQueries.push({
      collection: 'Business',
      where: buildWhere('bizState'),
      limit: RESULT_LIMIT,
      storeAs: STORE_KEY_BIZ,
    });
    if (activeQuery.state) {
      firestoreQueries.push({
        collection: 'Business',
        where: buildWhere('importState'),
        limit: RESULT_LIMIT,
        storeAs: STORE_KEY_IMP,
      });
    }
  }

  useFirestoreConnect(firestoreQueries);

  const bizVenues = useSelector(
    ({ firestore: { ordered } }) => ordered[STORE_KEY_BIZ]
  );
  const impVenues = useSelector(
    ({ firestore: { ordered } }) => ordered[STORE_KEY_IMP]
  );

  const loaded = activeQuery
    ? isLoaded(bizVenues) && (!activeQuery.state || isLoaded(impVenues))
    : true;

  // Merge, deduplicate, and optionally client-side filter on name
  const venues = (() => {
    if (!activeQuery || !loaded) return [];
    const all = [...(bizVenues || [])];
    if (impVenues) {
      const seen = new Set(all.map((v) => v.id));
      for (const v of impVenues) {
        if (!seen.has(v.id)) all.push(v);
      }
    }
    if (activeQuery.name && activeQuery.suburb) {
      const lower = activeQuery.name.toLowerCase();
      return all.filter(
        (v) => v.bizName && v.bizName.toLowerCase().startsWith(lower)
      );
    }
    return all;
  })();

  const searchPerformed = activeQuery && loaded;
  const loading = activeQuery && !loaded;

  // Move focus to results region when results arrive
  useEffect(() => {
    if (searchPerformed && resultsRef.current) {
      resultsRef.current.focus();
    }
  }, [searchPerformed]);

  const handleSearch = () => {
    const trimmedSuburb = suburb.trim();
    const trimmedName = venueName.trim();
    if (!state && !trimmedSuburb && !trimmedName) return;
    setActiveQuery({
      state: state || null,
      suburb: trimmedSuburb ? toTitleCase(trimmedSuburb) : null,
      name: trimmedName ? toTitleCase(trimmedName) : null,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const hasInput = state || suburb.trim() || venueName.trim();

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
      <SEO
        title="Venues"
        description="Browse restaurants, cafes and accessible dining venues on Piatto. Search by suburb, state or venue name."
        url="https://piatto.com.au/venues"
      />

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Explore
          sx={{ fontSize: 60, color: 'primary.main', mb: 2 }}
          aria-hidden="true"
        />
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{ fontWeight: 700 }}
        >
          Explore Venues
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          Find restaurants, cafes and accessible dining near you
        </Typography>
      </Box>

      {/* Search card */}
      <Card
        sx={{ borderRadius: 2, boxShadow: 3, mb: 4 }}
        role="search"
        aria-label="Venue search"
      >
        <CardContent sx={{ p: 4 }}>
          {/* State + Suburb row */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              select
              label="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
              sx={{ minWidth: 200, flex: 1 }}
            >
              <MenuItem value="">All states</MenuItem>
              {AUSTRALIAN_STATES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label} — {s.value}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Suburb or City"
              value={suburb}
              onChange={(e) => setSuburb(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. Brisbane, Melbourne"
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn aria-hidden="true" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Venue name + search button */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              label="Venue name"
              value={venueName}
              onChange={(e) => setVenueName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. The Coffee Shop"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search aria-hidden="true" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              size="large"
              onClick={handleSearch}
              disabled={!hasInput || loading}
              aria-busy={loading}
              aria-label="Search for venues"
              sx={{ minWidth: 120 }}
            >
              {loading ? (
                <CircularProgress size={24} aria-hidden="true" />
              ) : (
                'Search'
              )}
            </Button>
          </Box>

          {/* Live region announces result status to screen readers */}
          <Box
            aria-live="polite"
            aria-atomic="true"
            ref={resultsRef}
            tabIndex={-1}
            sx={{ outline: 'none' }}
          >
            {searchPerformed && venues.length === 0 && (
              <Alert severity="info">
                No venues found. Try broadening your search or using different
                terms.
              </Alert>
            )}

            {venues.length > 0 && (
              <Box>
                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  {venues.length >= RESULT_LIMIT
                    ? `Showing first ${RESULT_LIMIT} results — try a more specific search`
                    : `Found ${venues.length} venue${venues.length !== 1 ? 's' : ''}`}
                </Typography>

                <List
                  sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                  aria-label="Venue results"
                >
                  {venues.map((venue, index) => {
                    const image =
                      venue.bizPic1 ||
                      venue.bizPic2 ||
                      venue.bizPic3 ||
                      null;
                    const cuisines = (venue.bizCuisines || []).slice(0, 3);
                    const subtitle = [venue.bizType?.typeName, ...cuisines]
                      .filter(Boolean)
                      .join(', ');
                    const location = [venue.bizSuburb, venue.bizState]
                      .filter(Boolean)
                      .join(', ');
                    const rating = venue.bizRating || venue.googleRating;

                    return (
                      <React.Fragment key={venue.id}>
                        {index > 0 && <Divider component="li" />}
                        <ListItem
                          component="a"
                          href={`/venues/${venue.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            history.push(`/venues/${venue.id}`);
                          }}
                          sx={{
                            py: 2,
                            px: 3,
                            textDecoration: 'none',
                            color: 'inherit',
                            '&:hover': { bgcolor: 'action.hover' },
                            '&:focus': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: -2,
                            },
                          }}
                          aria-label={[
                            venue.bizName,
                            subtitle,
                            location && `in ${location}`,
                            rating &&
                              `Rated ${parseFloat(rating).toFixed(1)} out of 5`,
                          ]
                            .filter(Boolean)
                            .join('. ')}
                        >
                          <ListItemAvatar>
                            <Avatar
                              src={image || undefined}
                              alt=""
                              variant="rounded"
                              sx={{ width: 56, height: 56, mr: 1 }}
                              aria-hidden="true"
                            >
                              🍽
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600 }}
                              >
                                {venue.bizName}
                              </Typography>
                            }
                            secondary={
                              <Box component="span" sx={{ display: 'block' }}>
                                {subtitle && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    component="span"
                                    sx={{ display: 'block' }}
                                  >
                                    {subtitle}
                                  </Typography>
                                )}
                                {location && (
                                  <Box
                                    component="span"
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <LocationOn
                                      fontSize="small"
                                      aria-hidden="true"
                                    />
                                    <Typography
                                      variant="body2"
                                      component="span"
                                    >
                                      {location}
                                    </Typography>
                                  </Box>
                                )}
                                {rating && (
                                  <Box
                                    component="span"
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <Star
                                      fontSize="small"
                                      sx={{ color: 'orange' }}
                                      aria-hidden="true"
                                    />
                                    <Typography
                                      variant="body2"
                                      component="span"
                                    >
                                      {parseFloat(rating).toFixed(1)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              </Box>
            )}
          </Box>

          {/* Instructions */}
          {!searchPerformed && (
            <Box sx={{ mt: 3, p: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography
                variant="subtitle2"
                component="h2"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                How to search:
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                1. Select a state or territory (optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                2. Enter a suburb or city name (optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                3. Enter a venue name (optional)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                4. Click Search to find matching venues
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Own a venue?{' '}
          <a
            href="/claim-venue/search"
            style={{ color: '#B91C1C' }}
            aria-label="Claim your venue on Piatto"
          >
            Claim it for free
          </a>
        </Typography>
      </Box>
    </Container>
  );
};

export default VenuesPage;
